/* Admin — Editor Workbench panel (Sep-2026).
 *
 * Embeds the standalone Publishing Editor Workbench (`/editor`) directly
 * inside the Admin console so full Admins and Publishing Editor accounts
 * don't have to leave Admin to reach it. Reuses the exact same
 * components/API calls as the dedicated `/editor` app — no duplicated
 * logic, no new backend surface. `require_editor_access` already allows
 * both `pro_writer` and `editor` scopes on every underlying endpoint.
 */
import React, { useState } from 'react';
import { BookOpen, Library, GitBranch, LibraryBig, ExternalLink, Sparkles, Activity, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';

import EditorDashboard from '../editor/EditorDashboard';
import EditorPublicationWorkbench from '../editor/EditorPublicationWorkbench';
import EditorGenreTreePanel from '../editor/EditorGenreTreePanel';
import EditorAllManuscriptsPanel from '../editor/EditorAllManuscriptsPanel';
import EditorAiNotesInboxPanel from '../editor/EditorAiNotesInboxPanel';
import EditorGenerationProgressPanel from '../editor/EditorGenerationProgressPanel';
import EditorExportImportPanel from '../editor/EditorExportImportPanel';

const SUB_TABS = [
  { key: 'manuscripts',  label: 'All Manuscripts',      icon: LibraryBig },
  { key: 'progress',     label: 'Generation Progress',  icon: Activity },
  { key: 'ai-notes',     label: 'Review Notes Inbox',   icon: Sparkles },
  { key: 'export-import', label: 'Export / Import',     icon: ArrowLeftRight },
  { key: 'queue',        label: 'Review Queue',         icon: BookOpen },
  { key: 'publications', label: 'Publication Workbench', icon: Library },
  { key: 'genres',       label: 'Genre Tree',            icon: GitBranch },
];

export default function EditorWorkbenchPanel() {
  const [sub, setSub] = useState('manuscripts');

  return (
    <section data-testid="admin-editor-workbench-panel" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[16px] font-semibold">Editor Workbench</div>
          <div className="text-[11.5px] text-muted-foreground">
            Same workbench as the dedicated Editor app — review queue, publication gates, and genre taxonomy.
          </div>
        </div>
        <a
          href="/editor"
          target="_blank"
          rel="noreferrer"
          data-testid="admin-editor-workbench-open-standalone"
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <ExternalLink className="h-3 w-3" /> Open standalone editor app
        </a>
      </div>

      <div className="flex gap-1 border-b border-border" data-testid="admin-editor-workbench-subtabs">
        {SUB_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSub(key)}
            data-testid={`admin-editor-workbench-tab-${key}`}
            className={cn(
              'inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-[11.5px] font-medium transition-colors',
              sub === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div data-testid="admin-editor-workbench-content">
        {sub === 'manuscripts' && <EditorAllManuscriptsPanel />}
        {sub === 'progress' && <EditorGenerationProgressPanel />}
        {sub === 'ai-notes' && <EditorAiNotesInboxPanel />}
        {sub === 'export-import' && <EditorExportImportPanel />}
        {sub === 'queue' && <EditorDashboard />}
        {sub === 'publications' && <EditorPublicationWorkbench />}
        {sub === 'genres' && <EditorGenreTreePanel />}
      </div>
    </section>
  );
}
